import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getRegiones } from '../../../../../services/roleoAutomaticoService';
import { useLanguage } from '../../../../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../../../../utils/createScopedI18n';
import { configCourtsI18n } from './i18n';

const normalizeStatus = (value) => {
  const status = String(value || '')
    .trim()
    .toLowerCase();

  if (status === 'inactive' || status === 'inactivo') return 'inactive';
  return 'active';
};

export default function CreateEditCourtModal({
  Modal,
  Input,
  ComboBox,
  show,
  mode = 'create',
  value,
  onChange,
  onClose,
  onSave,
  Dropdown,
}) {
  const { language } = useLanguage();
  const courtsLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: configCourtsI18n });

  const isEdit = mode === 'edit';
  const [local, setLocal] = useState({
    id: null,
    name: '',
    status: 'active',
    regionId: null,
    regionAlias: '',
  });
  const [regions, setRegions] = useState([]);
  const [regionsLoading, setRegionsLoading] = useState(false);

  useEffect(() => {
    configCourtsI18n.changeLanguage(courtsLanguage);
  }, [courtsLanguage]);

  const statusOptions = useMemo(
    () => [
      { label: t('config_courts_status_active'), value: 'active' },
      { label: t('config_courts_status_inactive'), value: 'inactive' },
    ],
    [t]
  );

  const statusOptionFrom = (currentValue) =>
    statusOptions.find((option) => option.value === normalizeStatus(currentValue)) ||
    statusOptions[0];

  const regionOptions = useMemo(() => {
    const mapped = (regions || [])
      .map((region) => {
        const optionValue =
          region.id ??
          region.value ??
          region.codigo ??
          region.codigoRegion ??
          region.regionId ??
          region.idRegion ??
          null;
        const optionLabel =
          region.alias ??
          region.name ??
          region.nombre ??
          region.label ??
          region.regionNombre ??
          region.nombreRegion ??
          region.regionAlias ??
          '';

        if (optionValue == null || !optionLabel) return null;
        return { label: optionLabel, value: optionValue };
      })
      .filter(Boolean);

    mapped.sort((a, b) =>
      a.label.localeCompare(b.label, courtsLanguage === 'enUS' ? 'en' : 'es', {
        sensitivity: 'base',
      })
    );

    const seen = new Set();
    return mapped.filter((option) => {
      if (seen.has(option.value)) return false;
      seen.add(option.value);
      return true;
    });
  }, [regions, courtsLanguage]);

  const regionOptionFrom = (regionId, regionLabel) => {
    if (!regionOptions.length) return null;
    if (regionId != null) {
      const normalizedId = String(regionId);
      const byId = regionOptions.find(
        (option) => String(option.value) === normalizedId
      );
      if (byId) return byId;
    }

    if (regionLabel) {
      const normalizedLabel = String(regionLabel).toLowerCase();
      return (
        regionOptions.find(
          (option) => String(option.label).toLowerCase() === normalizedLabel
        ) || null
      );
    }

    return null;
  };

  useEffect(() => {
    if (!show) return;

    setLocal({
      id: value?.id ?? null,
      name: value?.name ?? '',
      status: normalizeStatus(value?.status),
      regionId: value?.regionId ?? value?.idRegion ?? null,
      regionAlias: value?.regionAlias ?? value?.region ?? '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, value?.id]);

  const nameValid = useMemo(() => local.name.trim().length > 0, [local.name]);
  const regionValid = useMemo(
    () =>
      !regionsLoading &&
      (regionOptions.length === 0 || local.regionId != null),
    [regionsLoading, regionOptions.length, local.regionId]
  );
  const isValid =
    nameValid &&
    regionValid &&
    ['active', 'inactive'].includes(normalizeStatus(local.status));

  const setField = (key) => (eventOrValue) => {
    const nextValue = eventOrValue?.target
      ? eventOrValue.target.value
      : eventOrValue;
    setLocal((prev) => ({ ...prev, [key]: nextValue }));
  };

  useEffect(() => {
    let mounted = true;

    const fetchRegions = async () => {
      if (!show) return;
      try {
        setRegionsLoading(true);
        const response = await getRegiones();
        if (!mounted) return;
        setRegions(Array.isArray(response) ? response : []);
      } catch (error) {
        console.error('Error loading regions:', error);
        if (mounted) {
          setRegions([]);
        }
      } finally {
        if (mounted) {
          setRegionsLoading(false);
        }
      }
    };

    fetchRegions();

    return () => {
      mounted = false;
    };
  }, [show]);

  const handleSave = () => {
    if (!isValid) return;
    const payload = {
      ...local,
      status: normalizeStatus(local.status),
    };
    onChange?.(payload);
    onSave?.(payload);
  };

  if (!Modal || !Input || !Dropdown) return null;

  return (
    <Modal
      title={
        isEdit ? t('config_courts_modal_title_edit') : t('config_courts_modal_title_create')
      }
      show={show}
      onClose={onClose}
      primaryBtnTxt={
        isEdit ? t('config_courts_modal_primary_edit') : t('config_courts_modal_primary_create')
      }
      primaryBtnAction={handleSave}
      disabledPrimaryBtn={!isValid}
      secondaryBtnTxt={t('config_courts_cancel')}
      secondaryBtnAction={onClose}
      ctaAligment='right'
      width='640px'
    >
      <div className='row g-3'>
        <div className='col-12'>
          <Input
            label={t('config_courts_modal_name_label')}
            required
            placeholder={t('config_courts_modal_name_placeholder')}
            value={local.name}
            onChange={setField('name')}
            error={local.name && !nameValid ? t('config_courts_modal_name_required') : ''}
          />
        </div>

        <div className='col-12 col-md-6'>
          <label className='form-label'>{t('config_courts_modal_status_label')}</label>
          <Dropdown
            options={statusOptions}
            value={statusOptionFrom(local.status)}
            onChange={(option) =>
              setLocal((prev) => ({ ...prev, status: option?.value || 'active' }))
            }
            placeholder={t('config_courts_modal_status_placeholder')}
            width='100%'
          />
        </div>

        <div className='col-12 col-md-6'>
          {ComboBox ? (
            <ComboBox
              label={t('config_courts_modal_region_label')}
              options={regionOptions}
              value={regionOptionFrom(local.regionId, local.regionAlias)}
              onChange={(option) =>
                setLocal((prev) => ({
                  ...prev,
                  regionId: option?.value ?? null,
                  regionAlias: option?.label ?? '',
                }))
              }
              placeholder={
                regionsLoading
                  ? t('config_courts_modal_region_loading')
                  : t('config_courts_modal_region_placeholder')
              }
              required
              isClearable
              disabled={regionsLoading || regionOptions.length === 0}
              isLoading={regionsLoading}
              width='100%'
            />
          ) : (
            <Dropdown
              label={t('config_courts_modal_region_label')}
              options={regionOptions}
              value={regionOptionFrom(local.regionId, local.regionAlias)}
              onChange={(option) =>
                setLocal((prev) => ({
                  ...prev,
                  regionId: option?.value ?? null,
                  regionAlias: option?.label ?? '',
                }))
              }
              placeholder={
                regionsLoading
                  ? t('config_courts_modal_region_loading')
                  : t('config_courts_modal_region_placeholder')
              }
              width='100%'
              isDisabled={regionsLoading || regionOptions.length === 0}
            />
          )}
        </div>
      </div>
    </Modal>
  );
}
